import React, { useState, useEffect, useCallback } from 'react'
import { Input, Button, Message, toaster, Loader, InputGroup } from 'rsuite'
import EyeIcon from '@rsuite/icons/legacy/Eye'
import EyeSlashIcon from '@rsuite/icons/legacy/EyeSlash'
import http from '../../../network/panel/http'
import './_settings.scss'

interface Setting {
  _id: string
  key: string
  value: string
  label?: string
  group?: string
}

const GROUP_LABELS: Record<string, string> = {
  ovh: 'OVHCloud',
  openai: 'OpenAI',
  caps: 'Капи',
  telegram: 'Telegram',
  general: 'General',
}

const GROUP_ORDER = ['telegram', 'ovh', 'openai', 'caps', 'general']

function isPlainTextSetting(setting: Setting) {
  return setting.group === 'telegram' || setting.key.endsWith('_INTERVAL_MIN')
}

export default function Settings() {
  const [settings, setSettings] = useState<Setting[]>([])
  const [editing, setEditing] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [visible, setVisible] = useState<Record<string, boolean>>({})

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await http.get('/settings')
      setSettings(data)
      const vals: Record<string, string> = {}
      data.forEach((s: Setting) => { vals[s.key] = s.value })
      setEditing(vals)
    } catch {
      toaster.push(<Message type="error">Failed to load settings</Message>, { duration: 3000 })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchSettings() }, [fetchSettings])

  const hasChanges = settings.some(s => editing[s.key] !== s.value)

  const handleSave = async () => {
    setSaving(true)
    try {
      const updates = settings
        .filter(s => editing[s.key] !== s.value)
        .map(s => ({ key: s.key, value: editing[s.key] || '' }))

      if (updates.length === 0) return

      await http.put('/settings', updates)
      toaster.push(<Message type="success">Settings saved</Message>, { duration: 2000 })
      await fetchSettings()
    } catch {
      toaster.push(<Message type="error">Failed to save settings</Message>, { duration: 3000 })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="settings-page"><Loader center size="md" /></div>

  const grouped = GROUP_ORDER
    .map(g => ({
      group: g,
      label: GROUP_LABELS[g] || g,
      items: settings.filter(s => (s.group || 'general') === g),
    }))
    .filter(g => g.items.length > 0)

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h2>Settings</h2>
        <Button
          appearance="primary"
          size="sm"
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        >
          Save
        </Button>
      </div>

      <div className="settings-groups">
        {grouped.map(g => (
          <div key={g.group} className="settings-group">
            <h4 className="settings-group-title">{g.label}</h4>
            <div className="settings-fields">
              {g.items.map(s => (
                <div key={s.key} className="settings-field">
                  <label>{s.label || s.key}</label>
                  <InputGroup inside>
                    <Input
                      type={isPlainTextSetting(s) || visible[s.key] ? 'text' : 'password'}
                      value={editing[s.key] || ''}
                      onChange={(val) => setEditing(prev => ({ ...prev, [s.key]: val }))}
                      placeholder={s.key}
                    />
                    {!isPlainTextSetting(s) && (
                      <InputGroup.Button
                        onClick={() => setVisible(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                      >
                        {visible[s.key] ? <EyeSlashIcon /> : <EyeIcon />}
                      </InputGroup.Button>
                    )}
                  </InputGroup>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
